import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ToastService } from '../../../../shared/services/toast.service';
import { extractDjangoErrors, formatDjangoErrors } from '../../../../shared/utils/django-errors';
import { fieldTypeLabel } from '../../../../shared/utils/field-type-label';
import { IMetadataKeyList, MetadataKind } from '../../../metadata-mgmt/models/Metadata-mgmt';
import { MetadataMgmtService } from '../../../metadata-mgmt/service/metadata-mgmt.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';

/**
 * Middle pane: lists templates for the currently selected source type.
 *
 * Watches `facade.selectedSourceTypeIdSignal` and refetches whenever the
 * sidebar selection changes. Each row shows the template name plus a
 * short rule-count summary ("3F / 1X" = 3 field, 1 cross-field) computed
 * from the already-loaded rule lists in the store.
 */
@Component({
  selector: 'app-template-list-panel',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './template-list-panel.component.html',
})
export class TemplateListPanelComponent {
  private metadataService = inject(MetadataMgmtService);
  private facade = inject(ValidationRulesFacade);
  private toast = inject(ToastService);

  templates = signal<IMetadataKeyList[]>([]);
  loading = signal(false);
  /** Baseline (pre-promotion) vs post-analysis fields — mirrors the metadata
   * management page toggle, scoping which templates (and therefore rules) are
   * shown. Lives in the store so the rule-editor modal scopes its pickers to
   * the same kind. */
  selectedKind = this.facade.selectedKindSignal;

  // Friendly data-type label ("Date", "Select", ...) for the per-row pill so
  // admins can see a field's type without opening the editor or the metadata
  // view.
  readonly fieldTypeLabel = fieldTypeLabel;

  selectedSourceTypeId = this.facade.selectedSourceTypeIdSignal;
  selectedTemplateId = this.facade.selectedTemplateIdSignal;

  private fieldRules = this.facade.fieldRulesSignal;
  private crossFieldRules = this.facade.crossFieldRulesSignal;

  countsByTemplateId = computed<Map<number, { field: number; cross: number }>>(() => {
    const result = new Map<number, { field: number; cross: number }>();
    for (const r of this.fieldRules()) {
      const entry = result.get(r.metadata_template) ?? { field: 0, cross: 0 };
      entry.field += 1;
      result.set(r.metadata_template, entry);
    }
    for (const r of this.crossFieldRules()) {
      for (const tid of [r.left_template, r.right_template]) {
        const entry = result.get(tid) ?? { field: 0, cross: 0 };
        entry.cross += 1;
        result.set(tid, entry);
      }
    }
    return result;
  });

  constructor() {
    // Effect: refetch templates whenever the source-type selection changes.
    // Effects run in injection context, so this is safe. The initial run
    // fires with the seeded `null` (Core) value, populating the list on
    // first paint without a separate ngOnInit hook.
    effect(() => {
      const sid = this.selectedSourceTypeId();
      const kind = this.selectedKind();
      this.fetchTemplatesFor(sid, kind);
    });
  }

  select(templateId: number): void {
    this.facade.selectTemplate(templateId);
  }

  selectKind(kind: MetadataKind): void {
    if (this.selectedKind() === kind) return;
    // The store clears the template selection on kind change; updating the
    // signal triggers the effect above to refetch the scoped template list.
    this.facade.selectKind(kind);
  }

  trackById(_: number, t: IMetadataKeyList): number {
    return t.id;
  }

  private fetchTemplatesFor(sourceTypeId: number | null, kind: MetadataKind): void {
    this.loading.set(true);
    // `getMetadataKey(undefined)` returns core templates (source_type IS NULL),
    // matching the backend filter. Pass the numeric id for per-source-type
    // scopes.
    const arg = sourceTypeId === null ? undefined : sourceTypeId;
    this.metadataService.getMetadataKey(arg, kind).subscribe({
      next: (rows) => {
        this.templates.set(rows ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        const formatted = formatDjangoErrors(extractDjangoErrors(err));
        this.toast.show(
          formatted && formatted !== 'An unexpected error occurred.'
            ? formatted
            : 'Failed to load templates',
          'error',
        );
      },
    });
  }
}
